if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.touchStartX = null;
        this.touchStartY = null;
        this.onTouchStartHandler = this.onTouchStart.bind(this);
        this.onTouchEndHandler = this.onTouchEnd.bind(this);
        this.onTouchCancelHandler = this.onTouchCancel.bind(this);
        this.onResizeHandler = debounce(() => this.fitActiveMedia(), 100);
        this.controlsWithClickHandlers = new WeakSet();

        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.refreshMedia();
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.resizeListenerConnected) return;

        window.addEventListener('resize', this.onResizeHandler);
        this.resizeListenerConnected = true;
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResizeHandler);
        this.resizeListenerConnected = false;
      }

      hide() {
        window.pauseAllMedia();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems.find((item) => item.dataset.mediaId === mediaId) || mediaItems[0];

        if (activeMedia) this.setActiveMedia(activeMedia.dataset.mediaId);
      }

      getMediaItems() {
        const mediaItems = Array.from(this.content?.querySelectorAll(':scope > [data-media-id]') || []);

        return mediaItems.filter(
          (item, index) => index === 0 || !item.classList.contains('product__media-item--variant')
        );
      }

      refreshMedia() {
        const content = this.querySelector('.product-media-modal__content');

        if (this.content !== content) {
          this.content?.removeEventListener('touchstart', this.onTouchStartHandler);
          this.content?.removeEventListener('touchend', this.onTouchEndHandler);
          this.content?.removeEventListener('touchcancel', this.onTouchCancelHandler);
          this.content = content;
          this.content?.addEventListener('touchstart', this.onTouchStartHandler, { passive: true });
          this.content?.addEventListener('touchend', this.onTouchEndHandler, { passive: true });
          this.content?.addEventListener('touchcancel', this.onTouchCancelHandler, { passive: true });
        }

        this.thumbnails = this.querySelector('.product-media-modal__thumbnails');
        this.status = this.querySelector('[data-modal-status]');
        this.querySelectorAll('[data-modal-nav], [data-media-target]').forEach((control) => {
          if (this.controlsWithClickHandlers.has(control)) return;

          control.addEventListener('click', () => {
            if (control.matches('[data-modal-nav]')) {
              this.navigate(control.dataset.modalNav);
            } else {
              this.setActiveMedia(control.dataset.mediaTarget);
            }
          });
          this.controlsWithClickHandlers.add(control);
        });

        const mediaItems = this.getMediaItems();
        const hasMultipleMedia = mediaItems.length > 1;
        this.querySelectorAll('[data-modal-nav]').forEach((button) => {
          button.hidden = !hasMultipleMedia;
        });
        if (this.thumbnails) this.thumbnails.hidden = !hasMultipleMedia;

        this.updateNavigation(mediaItems);
      }

      setActiveMedia(mediaId) {
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems.find((item) => item.dataset.mediaId === String(mediaId));

        if (!activeMedia) return;
        window.pauseAllMedia();

        Array.from(this.content.children).forEach((item) => {
          item.classList.remove('active');
          item.setAttribute('aria-hidden', 'true');
        });
        activeMedia.classList.add('active');
        activeMedia.removeAttribute('aria-hidden');

        let activeThumbnail = null;
        this.thumbnails?.querySelectorAll('[data-media-target]').forEach((thumbnail) => {
          if (thumbnail.dataset.mediaTarget === String(mediaId)) {
            activeThumbnail = thumbnail;
            thumbnail.setAttribute('aria-current', 'true');
            const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
            thumbnail.scrollIntoView({ behavior: scrollBehavior, block: 'nearest', inline: 'center' });
          } else {
            thumbnail.removeAttribute('aria-current');
          }
        });

        const activePosition = activeThumbnail?.dataset.mediaPosition;
        const activeMediaType = activeThumbnail?.dataset.mediaType;
        let statusTemplate = window.accessibilityStrings?.imageAvailable;
        if (activeMediaType === 'video' || activeMediaType === 'external_video') {
          statusTemplate = window.accessibilityStrings?.videoAvailable;
        } else if (activeMediaType === 'model') {
          statusTemplate = window.accessibilityStrings?.modelAvailable;
        }
        if (this.status) {
          this.status.textContent = activePosition && statusTemplate ? statusTemplate.replace('[index]', activePosition) : '';
        }

        this.updateNavigation(mediaItems, activeMedia);
        this.fitActiveMedia(activeMedia);
        this.playActiveMedia(activeMedia);
        this.refreshFocusTrap();
      }

      playActiveMedia(activeMedia) {
        if (!activeMedia.matches('deferred-media')) return;

        if (activeMedia.hasNativeVideo()) {
          activeMedia
            .prepareVideo(false)
            .then((video) => {
              if (!video || !this.hasAttribute('open') || !activeMedia.classList.contains('active')) return;
              video.play().catch(() => {});
            })
            .catch(() => {});
          return;
        }

        activeMedia.loadContent(false);
        const youtubeVideo = activeMedia.querySelector('.js-youtube');
        const vimeoVideo = activeMedia.querySelector('.js-vimeo');

        youtubeVideo?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        vimeoVideo?.contentWindow?.postMessage('{"method":"play"}', '*');
      }

      fitActiveMedia(activeMedia = this.content?.querySelector(':scope > .active')) {
        const isExternalVideo = activeMedia?.matches(
          'deferred-media[data-video-provider="youtube"], deferred-media[data-video-provider="vimeo"]'
        );
        const isModel = activeMedia?.classList.contains('product-media-modal__model');
        if ((!isExternalVideo && !isModel) || !this.content) return;

        const contentStyle = getComputedStyle(this.content);
        const availableWidth =
          this.content.clientWidth - parseFloat(contentStyle.paddingLeft) - parseFloat(contentStyle.paddingRight);
        const availableHeight =
          this.content.clientHeight - parseFloat(contentStyle.paddingTop) - parseFloat(contentStyle.paddingBottom);
        const aspectRatio = isModel
          ? 1
          : parseFloat(getComputedStyle(activeMedia).getPropertyValue('--media-aspect-ratio')) || 16 / 9;

        let width = Math.min(availableWidth, 1200);
        let height = width / aspectRatio;
        if (height > availableHeight) {
          height = availableHeight;
          width = height * aspectRatio;
        }

        activeMedia.style.setProperty('--modal-media-width', `${Math.max(width, 0)}px`);
        activeMedia.style.setProperty('--modal-media-height', `${Math.max(height, 0)}px`);
      }

      navigate(direction) {
        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('active'));
        const offset = direction === 'previous' ? -1 : 1;
        const nextMedia = mediaItems[activeIndex + offset];

        if (nextMedia) this.setActiveMedia(nextMedia.dataset.mediaId);
      }

      updateNavigation(mediaItems = this.getMediaItems(), activeMedia = null) {
        const activeIndex = mediaItems.findIndex((item) => item === activeMedia || item.classList.contains('active'));
        const previousButton = this.querySelector('[data-modal-nav="previous"]');
        const nextButton = this.querySelector('[data-modal-nav="next"]');
        const previousDisabled = activeIndex <= 0;
        const nextDisabled = activeIndex === -1 || activeIndex >= mediaItems.length - 1;

        if (previousButton && previousDisabled && document.activeElement === previousButton) {
          (nextButton && !nextDisabled ? nextButton : this.querySelector('[id^="ModalClose-"]'))?.focus();
        }
        if (nextButton && nextDisabled && document.activeElement === nextButton) {
          (previousButton && !previousDisabled ? previousButton : this.querySelector('[id^="ModalClose-"]'))?.focus();
        }

        if (previousButton) previousButton.disabled = previousDisabled;
        if (nextButton) nextButton.disabled = nextDisabled;
      }

      refreshFocusTrap() {
        if (!this.hasAttribute('open')) return;

        const activeElement = this.contains(document.activeElement)
          ? document.activeElement
          : this.querySelector('[role="dialog"]');
        trapFocus(this, activeElement);
      }

      onKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target.closest('video, iframe, model-viewer, input, select, textarea')) return;

        event.preventDefault();
        this.navigate(event.key === 'ArrowLeft' ? 'previous' : 'next');
      }

      onTouchStart(event) {
        if (event.target.closest('deferred-media, product-model')) {
          this.onTouchCancel();
          return;
        }
        this.touchStartX = event.changedTouches[0]?.clientX ?? null;
        this.touchStartY = event.changedTouches[0]?.clientY ?? null;
      }

      onTouchEnd(event) {
        if (this.touchStartX === null) return;

        const touchEndX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const touchEndY = event.changedTouches[0]?.clientY ?? this.touchStartY;
        const horizontalDistance = touchEndX - this.touchStartX;
        const verticalDistance = touchEndY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(horizontalDistance) < 50 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;
        this.navigate(horizontalDistance > 0 ? 'previous' : 'next');
      }

      onTouchCancel() {
        this.touchStartX = null;
        this.touchStartY = null;
      }
    }
  );
}
